import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Wallet, Coins, ArrowUpRight, Activity, Loader2, Check, AlertCircle, Plus, Shield, Droplets, Bot, Lock, Unlock } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { blockchainLedgerApi } from "@/api/blockchain-ledger";
import { queryKeys } from "@/lib/queryKeys";

interface BlockchainWalletCardProps {
  companyId: string;
}

export function BlockchainWalletCard({ companyId }: BlockchainWalletCardProps) {
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [mintAmount, setMintAmount] = useState<string>("");

  const { data: treasury } = useQuery({
    queryKey: queryKeys.blockchain.treasury(companyId),
    queryFn: () => blockchainLedgerApi.getTreasury(companyId),
    enabled: !!companyId,
    retry: 1,
  });

  const { data: tokenData } = useQuery({
    queryKey: queryKeys.blockchain.tokens(companyId),
    queryFn: () => blockchainLedgerApi.getTokenBalances(companyId),
    enabled: !!companyId,
    retry: 1,
  });

  const { data: txData } = useQuery({
    queryKey: queryKeys.blockchain.transactions(companyId),
    queryFn: () => blockchainLedgerApi.getTransactions(companyId, 10),
    enabled: !!companyId,
    retry: 1,
  });

  const { data: tokenStatus } = useQuery({
    queryKey: ["blockchain", "token-status", companyId],
    queryFn: () => blockchainLedgerApi.getTokenStatus(companyId),
    enabled: !!companyId,
    retry: 1,
  });

  const { data: onboardStatus } = useQuery({
    queryKey: ["blockchain", "onboard-status", companyId],
    queryFn: () => blockchainLedgerApi.getOnboardStatus(companyId),
    enabled: !!companyId,
    retry: 1,
  });

  const { data: network } = useQuery({
    queryKey: queryKeys.blockchain.network,
    queryFn: () => blockchainLedgerApi.getNetwork(),
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const createTokenMutation = useMutation({
    mutationFn: () => blockchainLedgerApi.createToken(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.blockchain.tokens(companyId) });
      queryClient.invalidateQueries({ queryKey: ["blockchain", "token-status", companyId] });
      queryClient.invalidateQueries({ queryKey: ["blockchain", "onboard-status", companyId] });
      setActionError(null);
    },
    onError: (err) => setActionError(err instanceof Error ? err.message : "Failed"),
  });

  const mintMutation = useMutation({
    mutationFn: (amount: number) => blockchainLedgerApi.mintAsa(companyId, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.blockchain.tokens(companyId) });
      queryClient.invalidateQueries({ queryKey: ["blockchain", "token-status", companyId] });
      setMintAmount("");
      setActionError(null);
    },
    onError: (err) => setActionError(err instanceof Error ? err.message : "Mint failed"),
  });

  const hasTreasury = !!treasury?.treasuryAddress;
  const hasTokens = (tokenData?.tokens?.length ?? 0) > 0;
  const hasTransactions = (txData?.transactions?.length ?? 0) > 0;
  const hasTokenStatus = tokenStatus?.hasToken;
  const isClaimed = tokenStatus?.asaClaimed ?? false;

  // Onboarding data
  const escrowAppId = tokenStatus?.escrowAppId ?? onboardStatus?.escrowAppId;
  const registryAppId = tokenStatus?.registryAppId ?? onboardStatus?.registryAppId;
  const ammAppId = tokenStatus?.ammAppId;
  const hasEscrow = !!escrowAppId;
  const hasAMM = !!ammAppId;
  const hasRegistry = !!registryAppId;
  const agentCount = onboardStatus?.agentCount ?? 0;

  return (
    <Card>
      <CardHeader className="px-5 pt-5 pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="h-4 w-4" />
          On-chain ledger
        </CardTitle>
        <CardDescription>
          Blockchain-verified cost, treasury, and token economy data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-5 pb-5 pt-2">
        {hasTreasury && (
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-border p-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1">
                <Wallet className="h-3 w-3" />
                Treasury
              </div>
              <div className="mt-2 text-lg font-semibold tabular-nums">
                {treasury.balance?.balanceEth ?? "0"} ETH
              </div>
              <div className="mt-1 text-xs text-muted-foreground font-mono truncate">
                {treasury.treasuryAddress}
              </div>
            </div>
            <div className="border border-border p-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1">
                <Activity className="h-3 w-3" />
                Network
              </div>
              <div className="mt-2 text-lg font-semibold tabular-nums">
                {network ? `Chain ${network.chainId}` : `Chain ${treasury.chainId}`}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {network ? network.name : (treasury.cached ? "Cached" : "Live")}
                {treasury && ` · ${new Date(treasury.updatedAt).toLocaleDateString()}`}
              </div>
            </div>
          </div>
        )}

        {/* Token + Claim Status */}
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1">
            <Coins className="h-3 w-3" />
            Company Token
          </div>

          {hasTokenStatus ? (
            <div className="border border-border px-3 py-2 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="h-3 w-3 text-emerald-500" />
                  <span className="text-sm font-medium">{tokenStatus.asa?.unitName}</span>
                  <span className="text-xs text-muted-foreground">{tokenStatus.asa?.assetName}</span>
                </div>
                <span className="text-xs font-mono text-muted-foreground">
                  ASA #{tokenStatus.asa?.assetId}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Supply: {tokenStatus.asa?.totalSupply}</span>
                <span className="flex items-center gap-1">
                  {isClaimed ? <Unlock className="h-3 w-3 text-emerald-500" /> : <Lock className="h-3 w-3 text-yellow-500" />}
                  {isClaimed ? "Claimed" : "Managed by Superboss"}
                </span>
              </div>
              {tokenStatus.algorandAddress && (
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                  Algorand: {tokenStatus.algorandAddress.slice(0, 12)}...
                </div>
              )}
              {/* Mint additional supply for AMM trading */}
              <div className="pt-2 border-t border-border space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Mint more for AMM
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder="Amount"
                    value={mintAmount}
                    onChange={(e) => setMintAmount(e.target.value)}
                    className="h-8 text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const amt = parseInt(mintAmount, 10);
                      if (amt > 0) mintMutation.mutate(amt);
                    }}
                    disabled={mintMutation.isPending || !mintAmount || parseInt(mintAmount, 10) <= 0}
                  >
                    {mintMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    Mint
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-border px-3 py-2 space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <AlertCircle className="h-3 w-3" />
                No token created yet
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => createTokenMutation.mutate()}
                disabled={createTokenMutation.isPending}
                className="w-full"
              >
                {createTokenMutation.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                Create Token
              </Button>
            </div>
          )}
        </div>

        {/* Escrow Status */}
        {hasEscrow && (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1">
              <Shield className="h-3 w-3" />
              Escrow (90% Company / 10% Network)
            </div>
            <div className="border border-border px-3 py-2 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">App ID</span>
                <span className="font-mono">{escrowAppId}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Company Share</span>
                <span className="text-emerald-500">90% (Locked)</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Network Share</span>
                <span className="text-blue-500">10% (OTC)</span>
              </div>
              {!isClaimed && (
                <div className="pt-1">
                  <Button size="sm" variant="outline" className="w-full h-7 text-xs" disabled>
                    <Lock className="h-3 w-3 mr-1" />
                    Claim escrow (passkey required)
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AMM Pool Status */}
        {hasAMM && (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1">
              <Droplets className="h-3 w-3" />
              AMM Pool
            </div>
            <div className="border border-border px-3 py-2 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">App ID</span>
                <span className="font-mono">{ammAppId}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Initial Price</span>
                <span className="font-semibold">$1.00 / token</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Fee Rate</span>
                <span>0.30%</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Liquidity</span>
                <span className="text-emerald-500">Active</span>
              </div>
            </div>
          </div>
        )}

        {/* Agent Registry */}
        {hasRegistry && (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1">
              <Bot className="h-3 w-3" />
              Agent Delegation
            </div>
            <div className="border border-border px-3 py-2 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Registry App</span>
                <span className="font-mono">{registryAppId}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Agents Registered</span>
                <span>{agentCount}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Trading Delegation</span>
                <span className={agentCount > 0 ? "text-emerald-500" : "text-muted-foreground"}>
                  {agentCount > 0 ? "Active" : "None"}
                </span>
              </div>
            </div>
          </div>
        )}

        {hasTokens && (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1">
              <Coins className="h-3 w-3" />
              All Tokens ({tokenData?.stats?.totalTokens ?? 0})
            </div>
            <div className="space-y-1">
              {tokenData?.tokens?.slice(0, 3).map((token) => (
                <div key={token.id} className="flex items-center justify-between border border-border px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{token.symbol}</span>
                    <span className="text-xs text-muted-foreground">{token.name}</span>
                  </div>
                  <div className="text-sm tabular-nums">
                    {token.balances?.treasury ?? "0"} / {token.totalSupply}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasTransactions && (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1">
              <ArrowUpRight className="h-3 w-3" />
              Recent transactions ({txData?.total ?? 0})
            </div>
            <div className="space-y-1">
              {txData?.transactions?.slice(0, 3).map((tx) => (
                <div key={tx.id} className="flex items-center justify-between border border-border px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground font-mono truncate max-w-[120px]">
                      {tx.txHash?.slice(0, 16)}...
                    </span>
                    <span className="text-xs capitalize">{tx.type}</span>
                  </div>
                  <span className={`text-xs ${tx.status === "confirmed" ? "text-emerald-500" : "text-yellow-500"}`}>
                    {tx.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {actionError && (
          <div className="text-xs text-destructive border border-destructive/20 p-2">
            {actionError}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
